import { Link } from "@tanstack/react-router";

export type FaqItem = { q: string; a: string };

/**
 * Builds FAQPage JSON-LD. Only ever call this with the exact same items that
 * are rendered by <FaqSection /> on the same page — Google requires the answer
 * text to be visible to the user.
 */
export function faqSchema(items: FaqItem[], url: string) {
  return {
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "@id": `${url}#faq`,
      url,
      mainEntity: items.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    }),
  };
}

/**
 * Visible FAQ accordion. Answers carry .aeo-answer for AI answer-box
 * extraction and stay in the DOM (native <details>) so crawlers always read
 * the text that the FAQPage JSON-LD declares.
 */
export function FaqSection({
  items,
  heading = "Frequently asked questions",
}: {
  items: FaqItem[];
  heading?: string;
}) {
  return (
    <section className="glass rounded-2xl p-6 mt-10">
      <h2 className="text-xl font-bold">{heading}</h2>
      <div className="mt-4 divide-y divide-border/50">
        {items.map((f, i) => (
          <details key={f.q} className="group py-3" open={i === 0}>
            <summary className="flex cursor-pointer list-none items-start justify-between gap-3 font-semibold marker:hidden">
              <span>{f.q}</span>
              <span
                aria-hidden="true"
                className="mt-0.5 shrink-0 text-primary transition-transform group-open:rotate-45"
              >
                +
              </span>
            </summary>
            <p className="aeo-answer mt-2 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}


/** Related-guide internal links, rendered under the FAQ on tool pages. */
export function RelatedGuides({ links }: { links: { slug: string; label: string }[] }) {
  return (
    <nav aria-label="Related Palworld guides" className="glass rounded-2xl p-6 mt-8">
      <h2 className="text-xl font-bold">Related Palworld guides</h2>
      <ul className="mt-3 grid gap-2 sm:grid-cols-2">
        {links.map((l) => (
          <li key={l.slug}>
            <Link
              to="/guides/$slug"
              params={{ slug: l.slug }}
              className="text-sm text-primary hover:underline"
            >
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}
